import { forwardRef, useId } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, Lock } from './icons.jsx';

export function Alert({ variant = 'error', children }) {
  return (
    <div className={`alert alert--${variant}`} role={variant === 'error' ? 'alert' : 'status'}>
      <span aria-hidden="true">{variant === 'error' ? '⚠' : 'ℹ'}</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * The centred block above every step: eyebrow pill, heading, sub. The heading
 * takes focus on each step change so keyboard and screen-reader users land on
 * the new question instead of at the top of the document.
 */
export const StepHeader = forwardRef(function StepHeader({ eyebrow, title, subtitle }, ref) {
  return (
    <div className="step-header">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h1 className="step-header__title" tabIndex={-1} ref={ref}>
        {title}
      </h1>
      {subtitle && <p className="step-header__subtitle">{subtitle}</p>}
    </div>
  );
});

/**
 * Progress indicator. `value` is 0–1. Purely visual for sighted users; the
 * same information is carried by the accessible name for everyone else.
 */
export function ProgressBar({ value, complete = false, label }) {
  const pct = Math.round(Math.min(Math.max(value, 0), 1) * 100);
  return (
    <div
      className="progress"
      data-complete={complete ? 'true' : undefined}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? `Journey ${pct}% complete`}
    >
      <span className="progress__fill" style={{ width: `${pct}%` }}>
        {!complete && <span className="progress__dot" aria-hidden="true" />}
      </span>
    </div>
  );
}

export function TrustRow() {
  return (
    <p className="trust">
      <span className="icon-well" aria-hidden="true">
        <Lock size={16} />
      </span>
      <span>
        Safe, Secure &amp; Confidential &nbsp;|&nbsp;{' '}
        <a href="/privacy">Privacy Policy</a>
      </span>
    </p>
  );
}

export function BackLink({ onClick }) {
  return (
    <button type="button" className="back-link" onClick={onClick}>
      <ArrowLeft size={18} />
      <span>Back</span>
    </button>
  );
}

export function Card({ children, className = '' }) {
  return <div className={`card ${className}`.trim()}>{children}</div>;
}

export const TextField = forwardRef(function TextField(
  { label, icon: Icon, trailingIcon: TrailingIcon, hint, error, className = '', id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  // A hint the sighted user can read has to reach a screen reader too, and it
  // is still worth announcing alongside an error — the error says what is
  // wrong, the hint says what is wanted.
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={fieldId}>
        {Icon && <Icon size={18} className="field__label-icon" />}
        <span>{label}</span>
        {hint && (
          <span className="field__hint" id={hintId}>
            {hint}
          </span>
        )}
      </label>
      <div className="field__well">
        <input
          ref={ref}
          id={fieldId}
          className={`field__control ${className}`.trim()}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          {...props}
        />
        {TrailingIcon && (
          <span className="field__trailing" aria-hidden="true">
            <TrailingIcon size={18} />
          </span>
        )}
      </div>
      {error && (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
});

export const SelectField = forwardRef(function SelectField(
  { label, icon: Icon, error, placeholder, children, id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;

  return (
    <div className="field">
      <label className="field__label" htmlFor={fieldId}>
        {Icon && <Icon size={18} className="field__label-icon" />}
        <span>{label}</span>
      </label>
      <div className="field__well">
        <select
          ref={ref}
          id={fieldId}
          className="field__control field__control--select"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {children}
        </select>
        <span className="field__trailing" aria-hidden="true">
          <ChevronDown size={18} />
        </span>
      </div>
      {error && (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
});

export function Checkbox({ label, checked, onChange, error, name }) {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <>
      <label className="checkbox" htmlFor={id}>
        <input
          id={id}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? 'true' : undefined}
          // Without this a screen-reader user is told the field is invalid but
          // never why — and this is the checkbox that blocks submission.
          aria-describedby={error ? errorId : undefined}
        />
        <span className="checkbox__box" aria-hidden="true" />
        <span className="checkbox__text">{label}</span>
      </label>
      {error && (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
    </>
  );
}

export function Button({
  children,
  loading,
  variant,
  type = 'button',
  disabled,
  icon: Icon = ArrowRight,
  ...props
}) {
  return (
    <button
      type={type}
      className={`button${variant ? ` button--${variant}` : ''}`}
      {...props}
      // After the spread, so an explicit `disabled` can never clobber the
      // loading guard and allow a double submit.
      disabled={loading || disabled}
    >
      {loading && <span className="button__spinner" aria-hidden="true" />}
      <span className="button__label">{loading ? 'Just a moment…' : children}</span>
      {!loading && Icon && (
        <span className="button__icon" aria-hidden="true">
          <Icon size={16} />
        </span>
      )}
    </button>
  );
}

/**
 * A selectable answer tile. Rendered as a button rather than a radio because
 * choosing one immediately advances the journey — there is no separate submit
 * for it to belong to.
 */
export function OptionCard({ option, selected, onSelect }) {
  const { label, icon: Icon, logo, tone } = option;
  return (
    <button
      type="button"
      className="option"
      data-tone={tone}
      data-selected={selected ? 'true' : undefined}
      data-media={Icon ? 'icon' : logo ? 'logo' : 'text'}
      onClick={() => onSelect(option.value)}
    >
      {Icon && (
        <span className="option__icon" aria-hidden="true">
          <Icon size={56} />
        </span>
      )}
      {logo && <img className="option__logo" src={logo} alt="" aria-hidden="true" />}
      {/* A provider tile is its logo — the Figma shows no caption under it. The
          name still has to reach a screen reader, so it stays in the accessible
          tree and only the visual duplicate is dropped. */}
      <span className={logo ? 'visually-hidden' : 'option__label'}>{label}</span>
    </button>
  );
}
